import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { MapPin, Phone, Mail, Globe, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";

const EMAILJS_SERVICE_ID = "service_l5m0wal";
const EMAILJS_TEMPLATE_ID = "template_4omkccj";
const EMAILJS_PUBLIC_KEY = "x8ZhCrs2X04K-x4rS";

const SERVICE_OPTIONS = [
  "Capacitación Laboral Inclusiva",
  "Orientación Social y Desarrollo de Talento",
  "Investigación en Ciencias Sociales",
  "Consultoría en Expansión y Comercialización",
  "Auditoría de Procesos y Gestión de Riesgos",
  "Otro / No estoy seguro",
];

export const Contact = () => {
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    empresa: "",
    telefono: "",
    servicio: "",
    mensaje: "",
  });

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      toast.error("Por favor completa los campos obligatorios.", {
        icon: <AlertCircle className="text-red-500" size={18} />,
      });
      return;
    }
    setLoading(true);
    try {
      const templateParams = {
        from_name: form.nombre,
        from_email: form.email,
        empresa: form.empresa,
        telefono: form.telefono,
        servicio: form.servicio,
        message: form.mensaje,
        reply_to: form.email,
      };
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      toast.success("¡Mensaje enviado con éxito! Nos pondremos en contacto pronto.", {
        icon: <CheckCircle2 className="text-green-500" size={18} />,
      });
      setForm({
        nombre: "",
        email: "",
        empresa: "",
        telefono: "",
        servicio: "",
        mensaje: "",
      });
    } catch (err) {
      console.error("EmailJS error:", err);
      toast.error("No se pudo enviar el mensaje. Inténtalo de nuevo o escríbenos directamente.", {
        icon: <AlertCircle className="text-red-500" size={18} />,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="py-24 lg:py-32 bg-white"
    >
      <div className="container-page">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#055794] font-semibold mb-5">
              Hablemos
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#022140] tracking-tight leading-[1.05] text-balance">
              Comencemos a impulsar tu empresa.
            </h2>
            <p className="mt-6 text-[17px] text-[#7F7F7F] leading-relaxed">
              Escríbenos. Nuestro equipo te contactará para entender los retos
              de tu negocio y diseñar una solución a la medida.
            </p>

            <div className="mt-10 space-y-5">
              <div
                className="flex items-start gap-4 p-5 rounded-2xl border border-slate-200 hover:border-[#72B3E4] transition-colors"
                data-testid="contact-info-address"
              >
                <span className="flex-shrink-0 h-11 w-11 rounded-xl bg-[#72B3E4]/15 text-[#055794] flex items-center justify-center">
                  <MapPin size={20} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#7F7F7F] font-semibold mb-1">
                    Dirección
                  </p>
                  <p className="text-[15px] text-[#022140] leading-relaxed">
                    Calle Topografos 635, int. 3, Arcos de Guadalupe, C.P.
                    45037, Zapopan, Jalisco, México.
                  </p>
                </div>
              </div>

              <a
                href="tel:+523326108716"
                className="flex items-start gap-4 p-5 rounded-2xl border border-slate-200 hover:border-[#72B3E4] transition-colors group"
                data-testid="contact-info-phone"
              >
                <span className="flex-shrink-0 h-11 w-11 rounded-xl bg-[#72B3E4]/15 text-[#055794] flex items-center justify-center group-hover:bg-[#055794] group-hover:text-white transition-colors">
                  <Phone size={20} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#7F7F7F] font-semibold mb-1">
                    Teléfono
                  </p>
                  <p className="text-[15px] text-[#022140] font-medium">
                    33 2 610 8716
                  </p>
                </div>
              </a>

              <a
                href="mailto:contacto@comisionempresarial.com"
                className="flex items-start gap-4 p-5 rounded-2xl border border-slate-200 hover:border-[#72B3E4] transition-colors group"
                data-testid="contact-info-email"
              >
                <span className="flex-shrink-0 h-11 w-11 rounded-xl bg-[#72B3E4]/15 text-[#055794] flex items-center justify-center group-hover:bg-[#055794] group-hover:text-white transition-colors">
                  <Mail size={20} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#7F7F7F] font-semibold mb-1">
                    Email
                  </p>
                  <p className="text-[15px] text-[#022140] font-medium">
                    contacto@comisionempresarial.com
                  </p>
                </div>
              </a>

              <div
                className="flex items-start gap-4 p-5 rounded-2xl border border-slate-200"
                data-testid="contact-info-web"
              >
                <span className="flex-shrink-0 h-11 w-11 rounded-xl bg-[#72B3E4]/15 text-[#055794] flex items-center justify-center">
                  <Globe size={20} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-[#7F7F7F] font-semibold mb-1">
                    Página Web
                  </p>
                  <p className="text-[15px] text-[#022140] font-medium">
                    comisionempresarial.com
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#72B3E4]/20 to-[#055794]/10 rounded-3xl blur-2xl" />
              <form
                ref={formRef}
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="relative bg-white border border-slate-200 rounded-3xl p-7 lg:p-10 shadow-[0_30px_60px_-30px_rgba(2,33,64,0.25)]"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-1">
                    <Label
                      htmlFor="nombre"
                      className="text-[#022140] font-medium text-sm mb-2 inline-block"
                    >
                      Nombre <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="nombre"
                      data-testid="contact-input-nombre"
                      value={form.nombre}
                      onChange={handleChange("nombre")}
                      placeholder="Tu nombre completo"
                      required
                      className="h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#055794] focus-visible:ring-offset-0"
                    />
                  </div>

                  <div className="sm:col-span-1">
                    <Label
                      htmlFor="email"
                      className="text-[#022140] font-medium text-sm mb-2 inline-block"
                    >
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={handleChange("email")}
                      placeholder="tucorreo@empresa.com"
                      required
                      className="h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#055794] focus-visible:ring-offset-0"
                    />
                  </div>

                  <div className="sm:col-span-1">
                    <Label
                      htmlFor="empresa"
                      className="text-[#022140] font-medium text-sm mb-2 inline-block"
                    >
                      Empresa
                    </Label>
                    <Input
                      id="empresa"
                      data-testid="contact-input-empresa"
                      value={form.empresa}
                      onChange={handleChange("empresa")}
                      placeholder="Nombre de tu empresa"
                      className="h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#055794] focus-visible:ring-offset-0"
                    />
                  </div>

                  <div className="sm:col-span-1">
                    <Label
                      htmlFor="telefono"
                      className="text-[#022140] font-medium text-sm mb-2 inline-block"
                    >
                      Teléfono
                    </Label>
                    <Input
                      id="telefono"
                      data-testid="contact-input-telefono"
                      type="tel"
                      value={form.telefono}
                      onChange={handleChange("telefono")}
                      placeholder="33 0000 0000"
                      className="h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#055794] focus-visible:ring-offset-0"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <Label
                      htmlFor="servicio"
                      className="text-[#022140] font-medium text-sm mb-2 inline-block"
                    >
                      Servicio de interés
                    </Label>
                    <Select
                      value={form.servicio}
                      onValueChange={handleChange("servicio")}
                    >
                      <SelectTrigger
                        id="servicio"
                        data-testid="contact-select-servicio"
                        className="h-12 rounded-xl border-slate-200 focus:ring-2 focus:ring-[#055794] focus:ring-offset-0"
                      >
                        <SelectValue placeholder="Selecciona un servicio" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((opt) => (
                          <SelectItem
                            key={opt}
                            value={opt}
                            data-testid={`contact-service-option-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="sm:col-span-2">
                    <Label
                      htmlFor="mensaje"
                      className="text-[#022140] font-medium text-sm mb-2 inline-block"
                    >
                      Mensaje <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="mensaje"
                      data-testid="contact-input-mensaje"
                      value={form.mensaje}
                      onChange={handleChange("mensaje")}
                      placeholder="Cuéntanos sobre tu empresa y cómo podemos ayudarte..."
                      required
                      rows={5}
                      className="rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#055794] focus-visible:ring-offset-0 resize-none"
                    />
                  </div>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-xs text-[#7F7F7F]">
                    Al enviar este formulario aceptas que nos contactemos
                    contigo.
                  </p>
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-form-submit"
                    className="group bg-[#022140] hover:bg-[#055794] text-white rounded-full h-12 px-7 font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#055794]/30 disabled:opacity-60 disabled:hover:translate-y-0"
                  >
                    {loading ? (
                      <>
                        <span className="inline-block h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin mr-2" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar mensaje
                        <Send
                          size={16}
                          className="ml-2 transition-transform group-hover:translate-x-1"
                        />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
